import styled from '@emotion/styled'
import {
  AppstoreOutlined,
  TrophyOutlined,
  ApartmentOutlined,
  PartitionOutlined,
} from '@ant-design/icons'
import { Checkbox, Select, Switch } from 'antd'
import { useFormikContext } from 'formik'
import { useEffect, useRef, type ReactNode } from 'react'
import {
  CLONE_TEAMS_OPTIONS,
  COMPETITION_TYPE_OPTIONS,
  isPlayoffsType,
  toCloneCompetitionType,
} from '@sportninja/common/features/competition/wizard'
import type {
  CloneTeamsMode,
  CompetitionType,
  WizardFormValues,
} from '@sportninja/common/features/competition/wizard'
import type { OrgCompetitionListItem } from '@sportninja/common/services/competition'
import ChoiceCard from '../components/ChoiceCard'
import SourceCompetitionPicker from '../components/SourceCompetitionPicker'
import { competitionWord } from '@sportninja/common/features/competition/wizard'
import Text from 'src/components/ts-components/Text'
import { paragraphSmall } from 'src/components/css'
import colors from '@sportninja/common/constants/appColors'

const TYPE_ICONS: Record<CompetitionType, ReactNode> = {
  season: <AppstoreOutlined style={{ fontSize: 32 }} />,
  playoffs_simple: <ApartmentOutlined style={{ fontSize: 32 }} />,
  playoffs_enhanced: <PartitionOutlined style={{ fontSize: 32 }} />,
  tournament: <TrophyOutlined style={{ fontSize: 32 }} />,
}

const TYPE_LEARN_MORE_URLS: Record<CompetitionType, string> = {
  season:
    'https://support.sportninja.com/set-up-and-edit-competitions-new-seasons-tournaments-etc',
  playoffs_simple:
    'https://support.sportninja.com/playoffs-/-tournaments-set-up-simple-version',
  playoffs_enhanced:
    'https://support.sportninja.com/playoffs-/-tournaments-set-up-enhanced-version',
  tournament:
    'https://support.sportninja.com/playoffs-/-tournaments-set-up-simple-version-0',
}

type Props = { orgId: string }

const Step3Type = ({ orgId }: Props) => {
  const { values, setFieldValue } = useFormikContext<WizardFormValues>()
  const cs = values.cloneSource
  // Simple Playoffs and Tournament share the optional-source treatment —
  // picker shown, "optional" suffix, no required validation. Enhanced
  // Playoffs is the only format whose source is required (no asterisk
  // anymore though; the validation message surfaces if the user skips it).
  const isTournament = values.competitionType === 'tournament'
  const isOptionalSourceType =
    isTournament || values.competitionType === 'playoffs_simple'
  const showSourcePicker =
    isPlayoffsType(values.competitionType) || isTournament
  // Tournament with a picked source mirrors the dedicated Clone screen:
  // Registration toggle + Clone Registration Settings checkbox + Clone Teams.
  // Registration ON hides Clone Teams (same as Step 1 Clone form behavior).
  const showRegistrationOptions = isTournament && Boolean(cs.sourceScheduleId)
  // Clone Teams selector only makes sense once the user has picked a source
  // competition — it's the source's teams we'd be cloning. Enhanced Playoffs
  // doesn't carry teams, so it stays hidden there. For Tournament, Registration
  // ON also hides it (registration competitions don't bring teams over).
  const showCloneTeams =
    (values.competitionType === 'playoffs_simple' ||
      (isTournament && !cs.isRegistrationSchedule)) &&
    Boolean(cs.sourceScheduleId)
  const canCloneRegistrationSettings = cs.isRegistrationSchedule
  const pickerRef = useRef<HTMLDivElement | null>(null)
  const cloneTeamsRef = useRef<HTMLDivElement | null>(null)
  const registrationRef = useRef<HTMLDivElement | null>(null)

  // Scroll the lowest currently-visible follow-up section into view whenever
  // it appears or changes. Picking Tournament reveals the picker; picking a
  // source then reveals the Registration block (and Clone Teams below it);
  // toggling Registration ON hides Clone Teams. In each transition we want
  // the bottom of the form visible — pick the last revealed ref and scroll.
  useEffect(() => {
    const target = showCloneTeams
      ? cloneTeamsRef.current
      : showRegistrationOptions
      ? registrationRef.current
      : showSourcePicker
      ? pickerRef.current
      : null
    target?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [showSourcePicker, showRegistrationOptions, showCloneTeams])

  const handleTypeChange = (next: CompetitionType) => {
    setFieldValue('competitionType', next)
    setFieldValue('isTournament', next === 'tournament')
    // Switching away from Playoffs *and* Tournament clears the source so we
    // don't leak state into formats that don't use the picker.
    if (!isPlayoffsType(next) && next !== 'tournament') {
      setFieldValue('cloneSource.sourceScheduleId', null)
      setFieldValue('cloneSource.sourceName', null)
    }
    // Clone Teams is only meaningful for Simple Playoffs and Tournament —
    // reset back to the default any time the user moves to a different card.
    if (next !== 'playoffs_simple' && next !== 'tournament') {
      setFieldValue('cloneSource.cloneTeams', 'none')
    }
    // Registration toggles only render for Tournament — clear them when
    // moving to any other card so the locked state matches the data.
    if (next !== 'tournament') {
      setFieldValue('cloneSource.isRegistrationSchedule', false)
      setFieldValue('cloneSource.cloneRegistrationSettings', false)
    }
  }

  const handleRegistrationToggle = (v: boolean) => {
    setFieldValue('cloneSource.isRegistrationSchedule', v)
    if (v) {
      // Registration competitions can't bring teams over — reset to default.
      setFieldValue('cloneSource.cloneTeams', 'none')
    } else {
      setFieldValue('cloneSource.cloneRegistrationSettings', false)
    }
  }

  const handleSourceChange = (item: OrgCompetitionListItem | null) => {
    // Pre-fill the cloneSource snapshot the same way the dedicated Clone step
    // does — keeps the eventual payload-builder happy.
    if (!item) {
      setFieldValue('cloneSource.sourceScheduleId', null)
      setFieldValue('cloneSource.sourceName', null)
      // Clearing the source returns us to the regular 6-step path; reset the
      // registration toggles so we don't carry them into a fresh selection.
      setFieldValue('cloneSource.isRegistrationSchedule', false)
      setFieldValue('cloneSource.cloneRegistrationSettings', false)
      return
    }
    setFieldValue('cloneSource.sourceScheduleId', item.id)
    setFieldValue('cloneSource.sourceName', item.name_full ?? null)
    setFieldValue('cloneSource.name', values.info.name || '')
    setFieldValue('cloneSource.startsAt', values.info.startsAt)
    setFieldValue('cloneSource.endsAt', values.info.endsAt)
    // Mirror the user-picked Step 3 format into cloneSource for payload reuse.
    setFieldValue(
      'cloneSource.competitionType',
      toCloneCompetitionType(values.competitionType)
    )
  }

  return (
    <Container>
      <Header>
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          {competitionWord()} Format
        </Text>
        <Text variant='paragraphMedium' color={colors.NEUTRAL_50}>
          Choose the format that best describes your{' '}
          {competitionWord({ capitalize: false })}.
        </Text>
      </Header>
      <Grid>
        {COMPETITION_TYPE_OPTIONS.map((opt) => (
          <ChoiceCard
            key={opt.value}
            icon={TYPE_ICONS[opt.value]}
            title={opt.label}
            description={opt.description}
            selected={values.competitionType === opt.value}
            learnMoreUrl={TYPE_LEARN_MORE_URLS[opt.value]}
            onClick={() => handleTypeChange(opt.value)}
            testId={`choice-type-${opt.value}`}
          />
        ))}
      </Grid>
      {showSourcePicker ? (
        <PickerWrapper ref={pickerRef}>
          <PickerLabel>
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              Choose your {competitionWord({ capitalize: false })}
            </Text>
            {isOptionalSourceType ? (
              <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
                Optional
              </Text>
            ) : null}
          </PickerLabel>
          <SourceCompetitionPicker
            orgId={orgId}
            value={cs.sourceScheduleId}
            onChange={handleSourceChange}
            sportId={values.info.sportId}
            genericSportId={values.info.genericSportId}
            placeholder={`Choose your ${competitionWord({
              capitalize: false,
            })}`}
          />
        </PickerWrapper>
      ) : null}
      {showRegistrationOptions ? (
        <RegistrationBlock ref={registrationRef}>
          <SwitchRow>
            <SwitchText>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                Registration {competitionWord()}
              </Text>
              <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
                Turn this ON if the cloned{' '}
                {competitionWord({ capitalize: false })} will be used for
                registration purposes.
              </Text>
            </SwitchText>
            <Switch
              size='small'
              checked={cs.isRegistrationSchedule}
              onChange={handleRegistrationToggle}
            />
          </SwitchRow>
          <Checkbox
            disabled={!canCloneRegistrationSettings}
            checked={cs.cloneRegistrationSettings}
            onChange={(e) =>
              setFieldValue(
                'cloneSource.cloneRegistrationSettings',
                e.target.checked
              )
            }
          >
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={
                canCloneRegistrationSettings ? colors.WHITE : colors.NEUTRAL_100
              }
            >
              Clone Registration Settings
            </Text>
          </Checkbox>
        </RegistrationBlock>
      ) : null}
      {showCloneTeams ? (
        <PickerWrapper ref={cloneTeamsRef}>
          <PickerLabel>
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              Clone Teams
            </Text>
          </PickerLabel>
          <Select
            style={{ width: '100%' }}
            options={CLONE_TEAMS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            value={cs.cloneTeams}
            onChange={(v) =>
              setFieldValue('cloneSource.cloneTeams', v as CloneTeamsMode)
            }
          />
        </PickerWrapper>
      ) : null}
    </Container>
  )
}

export default Step3Type

// ---- styled ----
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  width: 100%;
  max-width: 890px;

  & > button {
    height: 270px;
  }
`

const PickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  max-width: 720px;

  .ant-select-single {
    height: auto !important;
    min-height: 48px !important;
  }
  .ant-select .ant-select-selector {
    ${paragraphSmall};
    background-color: ${colors.SECONDARY_300} !important;
    border: 1px solid ${colors.SECONDARY_200} !important;
    border-radius: 16px !important;
    min-height: 48px !important;
    padding: 8px 16px !important;
    color: ${colors.WHITE} !important;
  }
  .ant-select-single .ant-select-selector .ant-select-selection-item,
  .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    display: flex !important;
    align-items: center !important;
    line-height: normal !important;
    padding-inline-start: 0 !important;
  }
  .ant-select-single .ant-select-selector .ant-select-selection-search,
  .ant-select-single .ant-select-selector .ant-select-selection-search-input {
    inset-inline-start: 16px !important;
    caret-color: transparent !important;
  }
  .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    color: ${colors.NEUTRAL_100} !important;
  }
  .ant-select .ant-select-arrow {
    color: ${colors.WHITE} !important;
    inset-inline-end: 16px !important;
  }
`

const PickerLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const RegistrationBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 720px;

  /* Mirror Step 1 Clone form's checkbox theming so disabled/checked states
     match across both entry points to a clone payload. */
  .ant-checkbox-wrapper {
    align-items: flex-start !important;
  }
  .ant-checkbox:not(.ant-checkbox-disabled) .ant-checkbox-inner {
    background-color: transparent !important;
    border-color: ${colors.WHITE} !important;
  }
  .ant-checkbox.ant-checkbox-disabled .ant-checkbox-inner {
    background-color: transparent !important;
    border-color: ${colors.SECONDARY_100} !important;
  }
  .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner {
    background-color: ${colors.PRIMARY} !important;
    border-color: ${colors.PRIMARY} !important;
  }
  .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner::after {
    border-color: ${colors.BLACK} !important;
  }
`

const SwitchRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`

const SwitchText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`
